import { useEffect, useRef, useState } from "react";
import { hostname } from "../../providers/api";
import { FINAL_UPLOAD_STATUSES, pollUploadHistory } from "./traceabilityVersionUpdate";

// A traceability upload links the project to its Version Set only when it succeeds (the
// backend replaces the data in one transaction). While it is queued or processing, its
// upload-history row carries the Version Set it is moving to (bcomeVersionId).
export const pendingVersionSetOf = (upload) =>
  upload?.bcomeVersionId && !FINAL_UPLOAD_STATUSES.includes(upload.status)
    ? upload.bcomeVersionId
    : null;

const fetchJson = async (url, accessToken) => {
  const response = await fetch(url, {
    headers: { authorization: "Bearer " + accessToken },
  });
  if (response.status >= 400) {
    throw new Error(`Request failed (HTTP ${response.status})`);
  }
  return response.json();
};

/**
 * Version Set of the project's traceability upload in progress (manual upload or
 * auto-upload Update Version), or null when none is running. Follows the upload until it
 * finishes, then calls onSettled(finalRow) so the page reloads the project: on success
 * it has the new Version Set, on failure it kept the previous one. `reloadKey` changes
 * when this page starts or finishes an upload.
 */
export const usePendingTraceabilityVersion = ({
  projectId,
  accessToken,
  reloadKey,
  onSettled,
}) => {
  const [pendingVersionSetId, setPendingVersionSetId] = useState(null);
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;

  useEffect(() => {
    if (!projectId) return undefined;
    let cancelled = false;

    const follow = async () => {
      try {
        const latest = await fetchJson(
          `${hostname}/projects/${projectId}/lastTraceabilityUpload`,
          accessToken
        );
        if (cancelled) return;
        const upload = latest?.upload;
        const pending = pendingVersionSetOf(upload);
        setPendingVersionSetId(pending);
        if (!pending) return;

        const finalRow = await pollUploadHistory({
          fetchRow: () => fetchJson(`${hostname}/uploadHistory/${upload.id}`, accessToken),
          isCancelled: () => cancelled,
        });
        // Polling gave up (timeout): the upload may still be running, keep it pending.
        if (cancelled || !finalRow) return;
        setPendingVersionSetId(null);
        onSettledRef.current?.(finalRow);
      } catch (error) {
        // The version shown falls back to the project's; nothing to block on.
        console.warn("[traceability] could not load the upload in progress", error);
      }
    };

    follow();
    return () => {
      cancelled = true;
    };
  }, [projectId, accessToken, reloadKey]);

  return pendingVersionSetId;
};
